import React, { FC, useRef, useState, useEffect, useCallback } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ComposedChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { Tooltip } from '../Tooltip';
import { MeterData } from '../../services/backend/SrcfulPublicApi';

// Custom tooltip for power consumption chart
const PowerConsumptionTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const date = new Date(label);
  const dateText = date.toLocaleString();

  return (
    <div className="bg-white/70 dark:bg-[#1D1D1D]/70 border border-gray-200/50 dark:border-[#292929]/50 rounded-[8px] p-3 shadow-lg max-w-[240px] whitespace-normal break-words backdrop-blur-none">
      <p className="text-gray-500 dark:text-[#9CA3AF] text-sm font-medium mb-1">{dateText}</p>
      {payload.map((entry: any, index: number) => (
        <div key={`item-${index}`} className="flex items-center gap-2 mt-1">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: entry.color }}
          ></div>
          <span className="text-gray-700 dark:text-[#E5E7EB] text-sm">{entry.name}:</span>
          <span className="text-gray-900 dark:text-[#E5E7EB] font-medium">
            {entry.name === 'Production' 
              ? Math.abs(entry.value).toFixed(3) 
              : entry.value.toFixed(3)} kW
            {entry.name === 'Net Consumption' && (
              <span className="text-xs text-gray-500 ml-1">
                {entry.value > 0 ? '(consuming)' : '(producing)'}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

// Custom tooltip for phase power chart
const PhaseConsumptionTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const date = new Date(label);
  const dateText = date.toLocaleString();
  
  return (
    <div className="bg-white/70 dark:bg-[#1D1D1D]/70 border border-gray-200/50 dark:border-[#292929]/50 rounded-[8px] p-3 shadow-lg max-w-[240px] whitespace-normal break-words backdrop-blur-none">
      <p className="text-gray-500 dark:text-[#9CA3AF] text-sm font-medium mb-1">{dateText}</p>
      {payload.map((entry: any, index: number) => (
        <div key={`item-${index}`} className="flex items-center gap-2 mt-1">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: entry.color }}
          ></div>
          <span className="text-gray-700 dark:text-[#E5E7EB] text-sm">{entry.name}:</span>
          <span className="text-gray-900 dark:text-[#E5E7EB] font-medium">{entry.value.toFixed(3)} kW</span>
        </div>
      ))}
    </div>
  );
};

interface CombinedPowerChartProps {
  selectedDay: {ts_utc: string, ts_gw: string, day_str: string} | null;
  dailyPowerData: MeterData['historical'];
  phasePowerData: MeterData['historical'];
  dailyPowerLoading: boolean;
  phasePowerLoading: boolean;
  resolution: '10s' | '1m' | '5m' | '10m' | '15m';
  onResolutionChange: (resolution: '10s' | '1m' | '5m' | '10m' | '15m') => void;
  showAggregatedView: boolean;
  onToggleAggregatedView: () => void;
  sn: string;
  formatSelectedDayForDisplay: (selectedDay: {ts_utc: string, ts_gw: string, day_str: string} | null) => string;
  gatewayTimezone?: string;
  onMouseMove?: (data: any, event: any) => void;
  onMouseLeave?: () => void;
  chartRef?: React.RefObject<any>;
}

// Helper function to get day boundaries for a date string (YYYY-MM-DD)
const getDayBoundaries = (dateStr: string | null | undefined) => {
  if (!dateStr) {
    // Return today's boundaries if no date is provided
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
    return { startOfDay, endOfDay };
  }
  
  const dateParts = dateStr.split('-');
  const year = parseInt(dateParts[0]);
  const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
  const day = parseInt(dateParts[2]);
  
  const startOfDay = new Date(year, month, day, 0, 0, 0, 0);
  const endOfDay = new Date(year, month, day, 23, 59, 59, 999);
  
  return { startOfDay, endOfDay };
};

export const CombinedPowerChart: FC<CombinedPowerChartProps> = ({
  selectedDay,
  dailyPowerData,
  phasePowerData,
  dailyPowerLoading,
  phasePowerLoading,
  resolution,
  onResolutionChange,
  showAggregatedView,
  onToggleAggregatedView,
  sn,
  formatSelectedDayForDisplay,
  gatewayTimezone,
  onMouseMove,
  onMouseLeave,
  chartRef
}) => {
  const zoomBarRef = useRef<HTMLDivElement>(null);

  // Separate refs for each chart to enable cross-chart tooltips
  const dailyChartRef = useRef<any>(null);
  const phaseChartRef = useRef<any>(null);

  // State for managing zoom domain
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<number>(0);
  const [dragMode, setDragMode] = useState<'pan' | 'resize-left' | 'resize-right'>('pan');

  // Reset zoom domain when selected day changes (but preserve zoom when resolution changes)
  useEffect(() => {
    setZoomDomain(null);
  }, [selectedDay]);

  // Process chart data for daily power
  const dailyChartData = [...dailyPowerData]
    .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
    .map(item => ({
      ts: new Date(item.ts).getTime(),
      consumption: item.consumption / 1000,
      delivery: -(item.delivery / 1000),
      netConsumption: (item.consumption - item.delivery) / 1000,
    }));

  // Process chart data for phase power
  const phaseChartData = [...phasePowerData]
    .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
    .map(item => ({
      ts: new Date(item.ts).getTime(),
      powerL1: (item.consumptionL1 + item.deliveryL1) / 1000 || 0,
      powerL2: (item.consumptionL2 + item.deliveryL2) / 1000 || 0,
      powerL3: (item.consumptionL3 + item.deliveryL3) / 1000 || 0
    }));

  // Calculate default domain
  const getDefaultDomain = (): [number, number] => {
    if (selectedDay) {
      const startTime = new Date(selectedDay.ts_utc);
      const endTime = new Date(startTime.getTime() + 24 * 60 * 60 * 1000);
      return [startTime.getTime(), endTime.getTime()];
    } else {
      const { startOfDay, endOfDay } = getDayBoundaries(null);
      return [startOfDay.getTime(), endOfDay.getTime()];
    }
  };

  // Get current domain (zoom domain or default)
  const currentDomain = zoomDomain || getDefaultDomain();
  const defaultDomain = getDefaultDomain();

  // Calculate zoom bar positions
  const getZoomBarPositions = () => {
    if (!zoomDomain) {
      return { left: 0, width: 100 };
    }
    
    const [domainStart, domainEnd] = defaultDomain;
    const [zoomStart, zoomEnd] = zoomDomain;
    const totalRange = domainEnd - domainStart;
    
    const leftPercent = ((zoomStart - domainStart) / totalRange) * 100;
    const widthPercent = ((zoomEnd - zoomStart) / totalRange) * 100;
    
    return {
      left: Math.max(0, Math.min(100 - widthPercent, leftPercent)),
      width: Math.max(5, Math.min(100, widthPercent))
    };
  };

  // Handle zoom bar mouse events
  const handleZoomBarMouseDown = useCallback((e: React.MouseEvent) => {
    if (!zoomBarRef.current) return;
    
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragMode('pan'); // Default to pan mode
    e.preventDefault();
  }, []);

  // Handle left edge drag
  const handleLeftEdgeMouseDown = useCallback((e: React.MouseEvent) => {
    if (!zoomBarRef.current) return;
    
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragMode('resize-left');
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering the main zoom bar handler
  }, []);

  // Handle right edge drag  
  const handleRightEdgeMouseDown = useCallback((e: React.MouseEvent) => {
    if (!zoomBarRef.current) return;
    
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragMode('resize-right');
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering the main zoom bar handler
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !zoomBarRef.current) return;
    
    const rect = zoomBarRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragStart;
    const deltaPercent = (deltaX / rect.width) * 100;
    
    const [domainStart, domainEnd] = defaultDomain;
    const totalRange = domainEnd - domainStart;
    const deltaTime = (deltaPercent / 100) * totalRange;
    
    const currentZoom = zoomDomain || [domainStart, domainEnd];
    const [currentStart, currentEnd] = currentZoom;
    
    if (dragMode === 'pan') {
      // Pan mode - move both edges together
      const duration = currentEnd - currentStart;
      
      let newStart = currentStart + deltaTime;
      let newEnd = currentEnd + deltaTime;
      
      // Clamp to bounds
      if (newStart < domainStart) {
        newStart = domainStart;
        newEnd = newStart + duration;
      }
      if (newEnd > domainEnd) {
        newEnd = domainEnd;
        newStart = newEnd - duration;
      }
      
      setZoomDomain([newStart, newEnd]);
    } else if (dragMode === 'resize-left') {
      // Resize left edge
      let newStart = currentStart + deltaTime;
      
      // Clamp to bounds and ensure minimum width
      newStart = Math.max(domainStart, Math.min(newStart, currentEnd - totalRange * 0.01)); // Min 1% width
      
      setZoomDomain([newStart, currentEnd]);
    } else if (dragMode === 'resize-right') {
      // Resize right edge
      let newEnd = currentEnd + deltaTime;
      
      // Clamp to bounds and ensure minimum width  
      newEnd = Math.min(domainEnd, Math.max(newEnd, currentStart + totalRange * 0.01)); // Min 1% width
      
      setZoomDomain([currentStart, newEnd]);
    }
    
    setDragStart(e.clientX);
  }, [isDragging, dragStart, zoomDomain, defaultDomain, dragMode]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragMode('pan');
  }, []);

  // Add mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Reset zoom function
  const resetZoom = () => {
    setZoomDomain(null);
  };

  // Cross-chart mouse event handlers for synchronized tooltips
  const handleDailyChartMouseMove = useCallback((data: any, event: any) => {
    // Call the original onMouseMove if provided
    if (onMouseMove) {
      onMouseMove(data, event);
    }
    
    // Trigger synthetic mousemove event on the phase chart
    if (data && data.activeLabel && phaseChartRef.current) {
      const phaseContainer = phaseChartRef.current;
      if (phaseContainer) {
        const chartWrapper = phaseContainer.querySelector('.recharts-wrapper');
        if (chartWrapper) {
          const rect = chartWrapper.getBoundingClientRect();
          const syntheticEvent = new MouseEvent('mousemove', {
            clientX: rect.left + (data.chartX || 0),
            clientY: rect.top + rect.height / 2,
            bubbles: true,
            cancelable: true
          });
          chartWrapper.dispatchEvent(syntheticEvent);
        }
      }
    }
  }, [onMouseMove]);

  const handleDailyChartMouseLeave = useCallback(() => {
    // Call the original onMouseLeave if provided
    if (onMouseLeave) {
      onMouseLeave();
    }
    
    // Trigger synthetic mouseleave event on the phase chart
    if (phaseChartRef.current) {
      const phaseContainer = phaseChartRef.current;
      if (phaseContainer) {
        const chartWrapper = phaseContainer.querySelector('.recharts-wrapper');
        if (chartWrapper) {
          const syntheticEvent = new MouseEvent('mouseleave', {
            bubbles: true,
            cancelable: true
          });
          chartWrapper.dispatchEvent(syntheticEvent);
        }
      }
    }
  }, [onMouseLeave]);

  const handlePhaseChartMouseMove = useCallback((data: any, event: any) => {
    // Call the original onMouseMove if provided
    if (onMouseMove) {
      onMouseMove(data, event);
    }
    
    // Trigger synthetic mousemove event on the daily chart
    if (data && data.activeLabel && dailyChartRef.current) {
      const dailyContainer = dailyChartRef.current;
      if (dailyContainer) {
        const chartWrapper = dailyContainer.querySelector('.recharts-wrapper');
        if (chartWrapper) {
          const rect = chartWrapper.getBoundingClientRect();
          const syntheticEvent = new MouseEvent('mousemove', {
            clientX: rect.left + (data.chartX || 0),
            clientY: rect.top + rect.height / 2,
            bubbles: true,
            cancelable: true
          });
          chartWrapper.dispatchEvent(syntheticEvent);
        }
      }
    }
  }, [onMouseMove]);

  const handlePhaseChartMouseLeave = useCallback(() => {
    // Call the original onMouseLeave if provided
    if (onMouseLeave) {
      onMouseLeave();
    }
    
    // Trigger synthetic mouseleave event on the daily chart
    if (dailyChartRef.current) {
      const dailyContainer = dailyChartRef.current;
      if (dailyContainer) {
        const chartWrapper = dailyContainer.querySelector('.recharts-wrapper');
        if (chartWrapper) {
          const syntheticEvent = new MouseEvent('mouseleave', {
            bubbles: true,
            cancelable: true
          });
          chartWrapper.dispatchEvent(syntheticEvent);
        }
      }
    }
  }, [onMouseLeave]);

  const zoomBarPositions = getZoomBarPositions();
  const isLoading = dailyPowerLoading || phasePowerLoading;

  return (
    <div className="bg-[#1D1D1D] rounded-[8px] px-3 pt-3 pb-1 border border-[#292929]">
      {/* Header with combined controls */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-white font-poly-sans-median">
            Power Analysis (kW)
            <span className="text-sm text-blue-400 font-normal ml-2">
              - {formatSelectedDayForDisplay(selectedDay)}
            </span>
            <span className="text-xs text-gray-500 font-normal ml-2">
              ({Math.max(dailyPowerData.length, phasePowerData.length)} points @ {resolution})
            </span>
          </h3>
          <Tooltip text="Combined view of total power flow\nand phase distribution over time">
            <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Separate</span>
          <button
            onClick={onToggleAggregatedView}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              showAggregatedView ? 'bg-blue-600' : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                showAggregatedView ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-xs text-gray-400">Net</span>
          
          {/* Reset Zoom Button */}
          {zoomDomain && (
            <button
              onClick={resetZoom}
              className="bg-[#404040] hover:bg-[#505050] border border-[#555] rounded-md px-2 py-1 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
            >
              Reset Zoom
            </button>
          )}
          
          {/* Resolution Selector */}
          <div className="flex items-center gap-2 ml-4">
            <span className="text-xs text-gray-400">Resolution:</span>
            <select
              value={resolution}
              onChange={(e) => onResolutionChange(e.target.value as '10s' | '1m' | '5m' | '10m' | '15m')}
              className="bg-[#292929] border border-[#404040] rounded-md px-2 py-1 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="10s">10s</option>
              <option value="1m">1m</option>
              <option value="5m">5m</option>
              <option value="10m">10m</option>
              <option value="15m">15m</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Combined Charts Container */}
      <div className="space-y-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-[400px]">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-gray-400 text-sm">Loading detailed data for {formatSelectedDayForDisplay(selectedDay)}...</p>
            </div>
          </div>
        ) : dailyPowerData.length > 0 || phasePowerData.length > 0 ? (
          <>
            {/* Daily Power Chart (Top) - No X-axis */}
            <div className="h-[200px] relative">
              {/* Legend for daily power */}
              <div className="absolute top-1 left-6 flex items-center gap-4 z-10">
                <div className="flex items-center gap-1 text-xs text-white font-medium pl-4">
                  <span>Power:</span>
                </div>
                {showAggregatedView ? (
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-t from-green-500 to-purple-500"></div>
                    <span className="text-xs text-[#E5E7EB]">Net Consumption</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <span className="text-xs text-[#E5E7EB]">Consumption</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-xs text-[#E5E7EB]">Production</span>
                    </div>
                  </>
                )}
              </div>
              
              <ResponsiveContainer width="100%" height={200} ref={dailyChartRef}>
                <ComposedChart
                  data={dailyChartData}
                  margin={{ top: 20, right: 0, left: -25, bottom: 5 }}
                  onMouseMove={handleDailyChartMouseMove}
                  onMouseLeave={handleDailyChartMouseLeave}
                >
                  <defs>
                    <linearGradient id="consumptionAreaGradient" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="rgba(0, 0, 0, 0)" />
                      <stop offset="80%" stopColor="rgba(156, 74, 215, 0.8)" />
                    </linearGradient>
                    <linearGradient id="productionAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="20%" stopColor="rgba(68, 164, 87, 0.1)" />
                      <stop offset="100%" stopColor="rgba(68, 164, 87, 0.8)" />
                    </linearGradient>
                    <linearGradient id="netGradient" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#44A457" />
                      <stop offset="100%" stopColor="#9C4AD7" />
                    </linearGradient>
                    <linearGradient id="netAreaGradient" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="rgba(68, 164, 87, 0.8)" />
                      <stop offset="50%" stopColor="rgba(0, 0, 0, 0)" />
                      <stop offset="100%" stopColor="rgba(156, 74, 215, 0.8)" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  {/* Hidden X-axis for top chart but with same config as bottom chart for grid alignment */}
                  <XAxis 
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={currentDomain}
                    tickFormatter={(ts) => new Date(ts).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    stroke="#555"
                    tick={{ fill: '#555', fontSize: '11px' }}
                    height={50}
                    tickMargin={20}
                    allowDataOverflow={true}
                    hide={true}
                  />
                  <YAxis 
                    stroke="#555"
                    tick={{ fill: '#555', fontSize: '11px' }}
                    tickFormatter={(value) => `${value.toFixed(1)}`}
                  />
                  <RechartsTooltip 
                    content={<PowerConsumptionTooltip />}
                    cursor={{
                      stroke: '#666',
                      strokeWidth: 1,
                      strokeOpacity: 0.5,
                      strokeDasharray: '3 3'
                    }}
                  />
                  {showAggregatedView ? (
                    <Area 
                      type="monotone" 
                      dataKey="netConsumption" 
                      fill="url(#netAreaGradient)"
                      stroke="url(#netGradient)"
                      strokeWidth={2}
                      name="Net Consumption"
                    />
                  ) : (
                    <>
                      <Area 
                        type="monotone" 
                        dataKey="consumption" 
                        fill="url(#consumptionAreaGradient)"
                        stroke="#9C4AD7"
                        strokeWidth={2}
                        name="Consumption"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="delivery" 
                        fill="url(#productionAreaGradient)"
                        stroke="#44A457"
                        strokeWidth={2}
                        name="Production"
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Separator line */}
            <div className="border-t border-[#404040] mx-6"></div>

            {/* Phase Power Chart (Bottom) - With X-axis */}
            <div className="h-[200px] relative">
              {/* Legend for phase power */}
              <div className="absolute top-1 left-6 flex items-center gap-4 z-10">
                <div className="flex items-center gap-1 text-xs text-white font-medium pl-4">
                  <span>Phase:</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-[#E5E7EB]">L1</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-xs text-[#E5E7EB]">L2</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-xs text-[#E5E7EB]">L3</span>
                </div>
              </div>
              
              <ResponsiveContainer width="100%" height={200} ref={phaseChartRef}>
                <LineChart
                  data={phaseChartData}
                  margin={{ top: 20, right: 0, left: -25, bottom: 5 }}
                  onMouseMove={handlePhaseChartMouseMove}
                  onMouseLeave={handlePhaseChartMouseLeave}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis 
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={currentDomain}
                    tickFormatter={(ts) => new Date(ts).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    stroke="#555"
                    tick={{ fill: '#555', fontSize: '11px' }}
                    height={50}
                    tickMargin={20}
                    allowDataOverflow={true}
                  />
                  <YAxis 
                    stroke="#555"
                    tick={{ fill: '#555', fontSize: '11px' }}
                    tickFormatter={(value) => `${value.toFixed(1)}`}
                  />
                  <RechartsTooltip
                    content={<PhaseConsumptionTooltip />}
                    cursor={{
                      stroke: '#666',
                      strokeWidth: 1,
                      strokeOpacity: 0.5,
                      strokeDasharray: '3 3'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="powerL1"
                    name="L1"
                    stroke="#22C55E"
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="powerL2"
                    name="L2"
                    stroke="#F97316"
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="powerL3"
                    name="L3"
                    stroke="#3B82F6"
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-[400px]">
            <p className="text-gray-400 text-sm">
              {selectedDay ? 'No data available for selected day' : 'No data available'}
            </p>
          </div>
        )}
      </div>

      {/* Custom Zoom Bar - positioned below the charts */}
      <div className="-mt-3 pl-8">
        <div 
          ref={zoomBarRef}
          className="relative h-6 bg-[#333] rounded-md select-none"
        >
          {/* Full range indicator */}
          <div className="absolute inset-1 bg-[#444] rounded-sm"></div>
          
          {/* Zoom selection */}
          <div 
            className="absolute top-1 bottom-1 bg-blue-500 rounded-sm cursor-move"
            style={{
              left: `${zoomBarPositions.left}%`,
              width: `${zoomBarPositions.width}%`
            }}
            onMouseDown={handleZoomBarMouseDown}
          >
            {/* Resize handles */}
            <div 
              className="absolute left-0 top-0 bottom-0 w-2 bg-blue-300 rounded-l cursor-ew-resize hover:bg-blue-200 transition-colors"
              onMouseDown={handleLeftEdgeMouseDown}
            ></div>
            <div 
              className="absolute right-0 top-0 bottom-0 w-2 bg-blue-300 rounded-r cursor-ew-resize hover:bg-blue-200 transition-colors"
              onMouseDown={handleRightEdgeMouseDown}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}; 